import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  Copy,
  Download,
  ChevronRight,
  Share2,
  Bell,
  RefreshCw,
  LogOut,
} from "lucide-react";
import {
  formatDatePt,
  formatTime,
  isPast,
  buildRsvpListText,
  buildWhatsAppShareText,
  buildRecap24hText,
  downloadCsv,
  hoursUntil,
} from "../lib/format";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import AdminNav from "../components/AdminNav";

const emptyForm = {
  name: "",
  location: "",
  location_link: "",
  meeting_point: "",
  meeting_point_link: "",
  ride_date: "",
  ride_time: "",
  arrival_time: "",
  departure_time: "",
  notes: "",
  video_link: "",
  is_official: true,
  show_rsvp_list_public: false,
  max_riders: "",
};

export default function AdminDashboardPage() {
  const isAdmin = !!localStorage.getItem("sf_admin_token");
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("sf_admin_token");
    toast.success("Sessão encerrada");
    navigate("/", { replace: true });
  };
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [rsvpDialog, setRsvpDialog] = useState({ open: false, ride: null, rsvps: [] });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null, // "ride" | "rsvp"
    id: null,
    label: "",
  });
  const [refreshingId, setRefreshingId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/rides");
      const sorted = [...(res.data || [])].sort((a, b) =>
        (b.ride_datetime || "").localeCompare(a.ride_datetime || "")
      );
      setRides(sorted);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    const dt = new Date(r.ride_datetime);
    const dateStr = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    const timeStr = dt.toTimeString().slice(0, 5);
    setEditingId(r.id);
    setForm({
      name: r.name || "",
      location: r.location || "",
      location_link: r.location_link || "",
      meeting_point: r.meeting_point || "",
      meeting_point_link: r.meeting_point_link || "",
      ride_date: dateStr,
      ride_time: timeStr,
      arrival_time: r.arrival_time || "",
      departure_time: r.departure_time || "",
      notes: r.notes || "",
      video_link: r.video_link || "",
      is_official: r.is_official !== false,
      show_rsvp_list_public: r.show_rsvp_list_public === true,
      max_riders: r.max_riders != null ? String(r.max_riders) : "",
    });
    setDialogOpen(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.name || !form.location || !form.meeting_point || !form.ride_date || !form.ride_time || !form.arrival_time || !form.departure_time) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }
    const iso = new Date(`${form.ride_date}T${form.ride_time}:00`).toISOString();
    const payload = {
      name: form.name,
      location: form.location,
      location_link: form.location_link,
      meeting_point: form.meeting_point,
      meeting_point_link: form.meeting_point_link,
      ride_datetime: iso,
      arrival_time: form.arrival_time,
      departure_time: form.departure_time,
      notes: form.notes,
      video_link: form.video_link,
      is_official: form.is_official,
      show_rsvp_list_public: form.show_rsvp_list_public,
      max_riders:
        form.max_riders !== "" && !isNaN(Number(form.max_riders))
          ? Math.max(1, parseInt(form.max_riders, 10))
          : null,
    };
    try {
      if (editingId) {
        await api.put(`/rides/${editingId}`, payload);
        toast.success("Rolê atualizado");
      } else {
        await api.post("/rides", payload);
        toast.success("Rolê criado");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const deleteRide = (ride) => {
    setDeleteDialog({
      open: true,
      type: "ride",
      id: ride.id,
      label: `Rolê: ${ride.name} · ${formatDatePt(ride.ride_datetime)}`,
    });
  };

  const confirmDelete = async (password) => {
    const headers = { "x-delete-password": password };
    if (deleteDialog.type === "ride") {
      await api.delete(`/rides/${deleteDialog.id}`, { headers });
      toast.success("Rolê removido");
      setDeleteDialog({ open: false, type: null, id: null, label: "" });
      load();
    } else if (deleteDialog.type === "rsvp") {
      await api.delete(`/rsvps/${deleteDialog.id}`, { headers });
      toast.success("Confirmado removido");
      setDeleteDialog({ open: false, type: null, id: null, label: "" });
      // Refresh the current ride's RSVPs if dialog is open
      if (rsvpDialog.open && rsvpDialog.ride) {
        const res = await api.get(`/rides/${rsvpDialog.ride.id}/rsvps`);
        setRsvpDialog({ ...rsvpDialog, rsvps: res.data || [] });
      }
    }
  };

  const requestDeleteRsvp = (rsvp) => {
    setDeleteDialog({
      open: true,
      type: "rsvp",
      id: rsvp.id,
      label: `Confirmado: ${rsvp.rider_name} (${rsvp.motorcycle_model})`,
    });
  };

  const openRsvps = async (ride) => {
    try {
      const res = await api.get(`/rides/${ride.id}/rsvps`);
      setRsvpDialog({ open: true, ride, rsvps: res.data || [] });
    } catch {
      toast.error("Erro ao carregar confirmados");
    }
  };

  const sendRecap = async (ride) => {
    try {
      const res = await api.get(`/rides/${ride.id}/rsvps`);
      const text = buildRecap24hText(ride, res.data || []);
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    } catch {
      toast.error("Erro ao gerar recap");
    }
  };

  const refreshWeather = async (ride) => {
    setRefreshingId(ride.id);
    try {
      await api.post(`/rides/${ride.id}/weather/refresh`);
      toast.success("Clima atualizado");
      load();
    } catch {
      toast.error("Falha ao atualizar clima");
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <AdminNav />
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-caps">Painel</div>
          <h1 className="font-display text-4xl sm:text-5xl text-white uppercase">
            Admin
          </h1>
          <p className="text-white/60 text-sm mt-2">
            Gerencie rolês e veja os confirmados.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={openCreate}
            className="btn-primary h-11 px-5 text-xs"
            data-testid="admin-create-ride-btn"
          >
            <Plus size={16} className="mr-2" /> Novo Rolê
          </Button>
          <button
            type="button"
            onClick={handleLogout}
            className="h-11 px-4 border border-white/15 hover:border-[#F59E0B] hover:text-[#F59E0B] text-white text-xs uppercase tracking-widest inline-flex items-center gap-2 transition-colors"
            title="Encerrar sessão do admin"
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="label-caps">Carregando...</div>
      ) : rides.length === 0 ? (
        <div
          className="border border-dashed border-white/10 bg-[#12141D] py-16 text-center"
          data-testid="admin-empty"
        >
          <div className="label-caps text-white/60">Nenhum rolê ainda</div>
          <p className="text-white text-lg mt-2 font-display uppercase">
            Cria o primeiro rolê e chama a galera
          </p>
        </div>
      ) : (
        <>
          {/* Desktop: Table */}
          <div className="border border-white/10 bg-[#12141D] overflow-x-auto hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="label-caps">Rolê</TableHead>
                  <TableHead className="label-caps">Data</TableHead>
                  <TableHead className="label-caps">Local</TableHead>
                  <TableHead className="label-caps">Status</TableHead>
                  <TableHead className="label-caps text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rides.map((r) => {
                  const past = isPast(r.ride_datetime);
                  const hUntil = hoursUntil(r.ride_datetime);
                  const within24h = !past && hUntil <= 24;
                  return (
                    <TableRow
                      key={r.id}
                      className="border-white/10 hover:bg-white/5"
                      data-testid={`admin-row-${r.id}`}
                    >
                      <TableCell className="text-white font-semibold">
                        {r.name}
                        {within24h && (
                          <span
                            className="ml-2 label-caps text-[10px] px-1.5 py-0.5 border animate-pulse"
                            style={{
                              color: "#F59E0B",
                              borderColor: "rgba(245,158,11,0.45)",
                            }}
                            data-testid={`admin-24h-badge-${r.id}`}
                          >
                            24h
                          </span>
                        )}
                        {r.is_official === false && (
                          <span
                            className="ml-2 label-caps text-[10px] px-1.5 py-0.5 border"
                            style={{
                              color: "#F59E0B",
                              borderColor: "rgba(245,158,11,0.35)",
                            }}
                            data-testid={`admin-unofficial-tag-${r.id}`}
                          >
                            Encontro Livre
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-white/80">
                        {formatDatePt(r.ride_datetime)}
                        <div className="text-white/50 text-xs">{r.arrival_time} → {r.departure_time}</div>
                      </TableCell>
                      <TableCell className="text-white/80 max-w-[240px] truncate">
                        {r.location}
                      </TableCell>
                      <TableCell>
                        {past ? (
                          <span className="label-caps text-white/60">Passado</span>
                        ) : (
                          <span className="label-caps" style={{ color: "#39FF14" }}>
                            Próximo
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <RideActions
                            r={r}
                            past={past}
                            within24h={within24h}
                            sendRecap={sendRecap}
                            openRsvps={openRsvps}
                            openEdit={openEdit}
                            deleteRide={deleteRide}
                            refreshWeather={refreshWeather}
                            refreshingId={refreshingId}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Mobile: Card list */}
          <div className="md:hidden space-y-3" data-testid="admin-cards-mobile">
            {rides.map((r) => {
              const past = isPast(r.ride_datetime);
              const hUntil = hoursUntil(r.ride_datetime);
              const within24h = !past && hUntil <= 24;
              return (
                <div
                  key={r.id}
                  className="border border-white/10 bg-[#12141D] p-4 rounded-sm"
                  data-testid={`admin-card-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-white font-semibold break-words">
                          {r.name}
                        </div>
                        {within24h && (
                          <span
                            className="label-caps text-[10px] px-1.5 py-0.5 border animate-pulse"
                            style={{
                              color: "#F59E0B",
                              borderColor: "rgba(245,158,11,0.45)",
                            }}
                            data-testid={`admin-24h-badge-${r.id}`}
                          >
                            24h
                          </span>
                        )}
                        {r.is_official === false && (
                          <span
                            className="label-caps text-[10px] px-1.5 py-0.5 border"
                            style={{
                              color: "#F59E0B",
                              borderColor: "rgba(245,158,11,0.35)",
                            }}
                            data-testid={`admin-unofficial-mobile-${r.id}`}
                          >
                            Encontro Livre
                          </span>
                        )}
                      </div>
                      <div className="mt-1">
                        {past ? (
                          <span className="label-caps text-white/60">Passado</span>
                        ) : (
                          <span className="label-caps" style={{ color: "#39FF14" }}>
                            Próximo
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-3 mb-3">
                    <div>
                      <div className="label-caps text-[10px]">Data</div>
                      <div className="text-white text-sm">{formatDatePt(r.ride_datetime)}</div>
                      <div className="text-white/50 text-xs mt-0.5">
                        {r.arrival_time} → {r.departure_time}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="label-caps text-[10px]">Local</div>
                      <div className="text-white/80 text-sm break-words">{r.location}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-white/10 flex-wrap">
                    <RideActions
                      r={r}
                      past={past}
                      within24h={within24h}
                      sendRecap={sendRecap}
                      openRsvps={openRsvps}
                      openEdit={openEdit}
                      deleteRide={deleteRide}
                      refreshWeather={refreshWeather}
                      refreshingId={refreshingId}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-2xl bg-[#12141D] border-white/10 max-h-[92vh] overflow-y-auto"
          data-testid="admin-ride-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-3xl uppercase text-white">
              {editingId ? "Editar Rolê" : "Novo Rolê"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-4">
            <TxtField
              label="Nome do Rolê *"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              testid="admin-name"
              placeholder="Ex: Rolê da Serra"
            />
            <TxtField
              label="Local *"
              value={form.location}
              onChange={(v) => setForm({ ...form, location: v })}
              testid="admin-location"
              placeholder="Ex: Restaurante do Mirante"
            />
            <TxtField
              label="Link do local (Maps, redes sociais, telefone, cardápio)"
              value={form.location_link}
              onChange={(v) => setForm({ ...form, location_link: v })}
              testid="admin-location-link"
              placeholder="https://..."
            />
            <TxtField
              label="Ponto de Encontro *"
              value={form.meeting_point}
              onChange={(v) => setForm({ ...form, meeting_point: v })}
              testid="admin-meeting-point"
              placeholder="Ex: Posto Shell BR-101"
            />
            <TxtField
              label="Link do Ponto de Encontro (Maps)"
              value={form.meeting_point_link}
              onChange={(v) => setForm({ ...form, meeting_point_link: v })}
              testid="admin-meeting-point-link"
              placeholder="https://maps..."
            />
            <div className="grid grid-cols-2 gap-4">
              <TxtField
                label="Data *"
                type="date"
                value={form.ride_date}
                onChange={(v) => setForm({ ...form, ride_date: v })}
                testid="admin-ride-date"
              />
              <TxtField
                label="Hora *"
                type="time"
                value={form.ride_time}
                onChange={(v) => setForm({ ...form, ride_time: v })}
                testid="admin-ride-time"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <TxtField
                label="Horário Chegada *"
                type="time"
                value={form.arrival_time}
                onChange={(v) => setForm({ ...form, arrival_time: v })}
                testid="admin-arrival-time"
              />
              <TxtField
                label="Horário Saída *"
                type="time"
                value={form.departure_time}
                onChange={(v) => setForm({ ...form, departure_time: v })}
                testid="admin-departure-time"
              />
            </div>
            <div>
              <Label className="label-caps block mb-2">Observações</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm min-h-[80px]"
                placeholder="Detalhes extras."
                data-testid="admin-notes"
              />
            </div>
            <TxtField
              label="Link do vídeo do rolê (para rolês passados)"
              value={form.video_link}
              onChange={(v) => setForm({ ...form, video_link: v })}
              testid="admin-video-link"
              placeholder="https://www.instagram.com/speed_freaks_race_team_/."
            />

            <div data-testid="admin-max-riders-field">
              <Label className="label-caps block mb-2">
                Limite de Motos (opcional)
              </Label>
              <Input
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={form.max_riders}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d]/g, "");
                  setForm({ ...form, max_riders: v });
                }}
                placeholder="Ex: 20 — deixe vazio para vagas ilimitadas"
                className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
                data-testid="admin-max-riders-input"
              />
              <p className="text-[11px] text-white/50 mt-1">
                Quando o número de confirmados atinge esse limite, os próximos entram automaticamente na <span className="text-[#F59E0B]">Fila de Espera</span>. Apagar um confirmado promove o 1º da fila.
              </p>
            </div>
            <label
              className="flex items-start gap-3 select-none cursor-pointer border border-white/10 bg-[#0B0D14] px-4 py-3"
              data-testid="admin-is-official-field"
            >
              <Checkbox
                checked={form.is_official}
                onCheckedChange={(v) => setForm({ ...form, is_official: !!v })}
                className="mt-0.5 border-white/30 data-[state=checked]:bg-[#00E5FF] data-[state=checked]:text-black"
                data-testid="admin-is-official-checkbox"
              />
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold">
                  Rolê Oficial
                </div>
                <div className="text-white/60 text-xs mt-1">
                  Desmarque para criar um <span className="text-[#F59E0B]">Encontro Livre</span> — não conta pontos no Ranking (anual/vitalício).
                </div>
              </div>
            </label>

            <label
              className="flex items-start gap-3 select-none cursor-pointer border border-white/10 bg-[#0B0D14] px-4 py-3"
              data-testid="admin-show-list-field"
            >
              <Checkbox
                checked={form.show_rsvp_list_public}
                onCheckedChange={(v) => setForm({ ...form, show_rsvp_list_public: !!v })}
                className="mt-0.5 border-white/30 data-[state=checked]:bg-[#39FF14] data-[state=checked]:text-black"
                data-testid="admin-show-list-checkbox"
              />
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold">
                  Liberar Lista de Inscritos para Pilotos
                </div>
                <div className="text-white/60 text-xs mt-1">
                  Quando ligado, todos os pilotos podem clicar em <span className="text-[#39FF14]">"Ver Lista de Presença"</span> no card do rolê. O acesso continua exigindo a validação do WhatsApp de quem já se inscreveu. Você pode ligar ou desligar a qualquer momento.
                </div>
              </div>
            </label>

            <DialogFooter className="gap-2">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="border border-white/15 text-white px-5 py-2.5 text-xs uppercase tracking-widest hover:border-white transition-colors"
                data-testid="admin-cancel-btn"
              >
                Cancelar
              </button>
              <Button
                type="submit"
                className="btn-primary h-10 px-6 text-xs"
                data-testid="admin-save-btn"
              >
                {editingId ? "Salvar" : "Criar Rolê"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* RSVPs dialog */}
      <Dialog
        open={rsvpDialog.open}
        onOpenChange={(o) => setRsvpDialog({ ...rsvpDialog, open: o })}
      >
        <DialogContent
          className="max-w-3xl bg-[#12141D] border-white/10 max-h-[92vh] overflow-y-auto"
          data-testid="admin-rsvp-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-3xl uppercase text-white">
              Confirmados
            </DialogTitle>
            <div className="text-white/60 text-sm">
              {(() => {
                const confirmed = rsvpDialog.rsvps.filter((r) => !r.waitlist_position).length;
                const waitlist = rsvpDialog.rsvps.filter((r) => (r.waitlist_position || 0) >= 1).length;
                const cap = rsvpDialog.ride?.max_riders;
                return (
                  <>
                    {rsvpDialog.ride?.name} · {confirmed}
                    {cap ? `/${cap}` : ""} confirmado(s)
                    {waitlist > 0 ? ` · ${waitlist} na fila` : ""}
                  </>
                );
              })()}
            </div>
          </DialogHeader>

          {rsvpDialog.ride && (
            <RsvpsPanel
              ride={rsvpDialog.ride}
              rsvps={rsvpDialog.rsvps}
              onRefresh={() => openRsvps(rsvpDialog.ride)}
              onRequestDelete={requestDeleteRsvp}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog (password-protected) */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(o) =>
          setDeleteDialog((d) => ({ ...d, open: o }))
        }
        title={deleteDialog.type === "ride" ? "Excluir Rolê" : "Excluir Confirmado"}
        description={
          deleteDialog.type === "ride"
            ? "Isso remove o rolê e todos os confirmados vinculados. Digite a senha para confirmar."
            : "Digite a senha de admin para remover este confirmado."
        }
        itemLabel={deleteDialog.label}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function RideActions({ r, past, within24h, sendRecap, openRsvps, openEdit, deleteRide, refreshWeather, refreshingId }) {
  const isRefreshing = refreshingId === r.id;
  return (
    <>
      {!past && (
        <button
          onClick={() => refreshWeather(r)}
          disabled={isRefreshing}
          className="border border-white/15 hover:border-[#00E5FF] p-2 rounded-sm transition-colors disabled:opacity-50"
          title="Atualizar clima"
          data-testid={`admin-refresh-weather-${r.id}`}
        >
          <RefreshCw size={14} className={isRefreshing ? "animate-spin" : ""} />
        </button>
      )}
      {!past && (
        <button
          onClick={() => sendRecap(r)}
          className="border p-2 rounded-sm transition-colors"
          style={{
            borderColor: within24h
              ? "rgba(245,158,11,0.55)"
              : "rgba(255,255,255,0.15)",
            color: within24h ? "#F59E0B" : undefined,
          }}
          title="Enviar recap no WhatsApp"
          data-testid={`admin-recap-${r.id}`}
        >
          <Bell size={14} />
        </button>
      )}
      <button
        onClick={() => openRsvps(r)}
        className="border border-white/15 hover:border-[#00E5FF] p-2 rounded-sm transition-colors"
        title="Ver confirmados"
        data-testid={`admin-view-rsvps-${r.id}`}
      >
        <Users size={14} />
      </button>
      <button
        onClick={() => openEdit(r)}
        className="border border-white/15 hover:border-white p-2 rounded-sm transition-colors"
        title="Editar"
        data-testid={`admin-edit-${r.id}`}
      >
        <Pencil size={14} />
      </button>
      <button
        onClick={() => deleteRide(r)}
        className="border border-white/15 hover:border-red-500 hover:text-red-400 p-2 rounded-sm transition-colors"
        title="Excluir"
        data-testid={`admin-delete-${r.id}`}
      >
        <Trash2 size={14} />
      </button>
    </>
  );
}

function TxtField({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div data-testid={`${testid}-field`}>
      <Label className="label-caps block mb-2">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[#0B0D14] border-white/10 focus:border-[#00E5FF] rounded-sm h-11"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

function RsvpsPanel({ ride, rsvps, onRefresh, onRequestDelete }) {
  const confirmedRsvps = rsvps.filter((r) => !r.waitlist_position);
  const waitlistRsvps = rsvps
    .filter((r) => (r.waitlist_position || 0) >= 1)
    .sort((a, b) => (a.waitlist_position || 0) - (b.waitlist_position || 0));
  const orderedRsvps = [...confirmedRsvps, ...waitlistRsvps];

  const copyText = async () => {
    const txt = buildRsvpListText(ride, rsvps);
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Texto copiado! Cola no WhatsApp.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const shareWhatsApp = () => {
    const txt = buildRsvpListText(ride, rsvps);
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };

  const sendRecap24h = () => {
    const txt = buildRecap24hText(ride, rsvps);
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };

  const exportCsv = () => {
    const rows = [
      ["Nome", "Telefone", "Modelo Moto", "Acompanhante", "Convidado", "Vaga", "Código", "Confirmado em"],
      ...orderedRsvps.map((r) => [
        r.rider_name,
        r.phone || "",
        r.motorcycle_model,
        r.companion || "",
        r.is_guest ? "Sim" : "Não",
        r.waitlist_position && r.waitlist_position >= 1
          ? `Fila #${r.waitlist_position}`
          : "Confirmado",
        r.confirmation_code || "",
        r.created_at,
      ]),
    ];
    downloadCsv(`confirmados-${ride.name.replace(/\s+/g, "-").toLowerCase()}.csv`, rows);
  };

  const deleteRsvp = (r) => {
    onRequestDelete(r);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={sendRecap24h}
          className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-sm transition-colors"
          style={{
            background: "#F59E0B",
            color: "#000",
          }}
          data-testid="rsvp-recap24h-btn"
        >
          <Bell size={14} /> Enviar Recap 24h
        </button>
        <button
          onClick={copyText}
          className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-xs"
          data-testid="rsvp-copy-btn"
        >
          <Copy size={14} /> Copiar Texto WhatsApp
        </button>
        <button
          onClick={shareWhatsApp}
          className="border border-white/15 hover:border-[#00E5FF] px-4 py-2 text-xs uppercase tracking-widest text-white inline-flex items-center gap-2 transition-colors"
          data-testid="rsvp-share-btn"
        >
          <Share2 size={14} /> Abrir WhatsApp
        </button>
        <button
          onClick={exportCsv}
          className="border border-white/15 hover:border-white px-4 py-2 text-xs uppercase tracking-widest text-white inline-flex items-center gap-2 transition-colors"
          data-testid="rsvp-csv-btn"
        >
          <Download size={14} /> CSV
        </button>
      </div>

      {rsvps.length === 0 ? (
        <div
          className="border border-dashed border-white/10 py-10 text-center label-caps text-white/50"
          data-testid="rsvp-empty"
        >
          Nenhum confirmado ainda
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="border border-white/10 overflow-x-auto hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="label-caps">#</TableHead>
                  <TableHead className="label-caps">Nome</TableHead>
                  <TableHead className="label-caps">Telefone</TableHead>
                  <TableHead className="label-caps">Moto</TableHead>
                  <TableHead className="label-caps">Acompanhante</TableHead>
                  <TableHead className="label-caps">Vaga</TableHead>
                  <TableHead className="label-caps">Tipo</TableHead>
                  <TableHead className="label-caps">Código</TableHead>
                  <TableHead className="label-caps text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedRsvps.map((r, idx) => (
                  <TableRow
                    key={r.id}
                    className="border-white/10"
                    data-testid={`rsvp-row-${r.id}`}
                  >
                    <TableCell className="text-white/60">{idx + 1}</TableCell>
                    <TableCell className="text-white font-semibold">{r.rider_name}</TableCell>
                    <TableCell className="text-white/80 whitespace-nowrap">{r.phone || "—"}</TableCell>
                    <TableCell className="text-white/80">{r.motorcycle_model}</TableCell>
                    <TableCell className="text-white/80">{r.companion || "—"}</TableCell>
                    <TableCell>
                      {r.waitlist_position && r.waitlist_position >= 1 ? (
                        <span
                          className="label-caps"
                          style={{ color: "#F59E0B" }}
                          data-testid={`rsvp-waitlist-${r.id}`}
                        >
                          Fila #{r.waitlist_position}
                        </span>
                      ) : (
                        <span className="label-caps" style={{ color: "#39FF14" }}>
                          Confirmado
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.is_guest ? (
                        <span className="label-caps" style={{ color: "#F59E0B" }}>
                          Convidado
                        </span>
                      ) : (
                        <span className="label-caps text-white/70">Membro</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.confirmation_code ? (
                        <span
                          className="font-display tracking-[0.2em] text-sm"
                          style={{ color: "#39FF14" }}
                          data-testid={`rsvp-code-${r.id}`}
                        >
                          {r.confirmation_code}
                        </span>
                      ) : (
                        <span className="text-white/40 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => deleteRsvp(r)}
                        className="border border-white/15 hover:border-red-500 hover:text-red-400 p-2 rounded-sm transition-colors"
                        data-testid={`rsvp-delete-${r.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile card list */}
          <div className="sm:hidden space-y-2" data-testid="rsvp-cards-mobile">
            {orderedRsvps.map((r, idx) => (
              <div
                key={r.id}
                className="border border-white/10 bg-[#0B0D14] p-3 rounded-sm"
                data-testid={`rsvp-card-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-white/50 text-xs">#{idx + 1}</span>
                      {r.waitlist_position && r.waitlist_position >= 1 ? (
                        <span
                          className="label-caps text-[10px] px-1.5 py-0.5 border"
                          style={{
                            color: "#F59E0B",
                            borderColor: "rgba(245,158,11,0.45)",
                          }}
                          data-testid={`rsvp-waitlist-mobile-${r.id}`}
                        >
                          Fila #{r.waitlist_position}
                        </span>
                      ) : (
                        <span
                          className="label-caps text-[10px] px-1.5 py-0.5 border"
                          style={{
                            color: "#39FF14",
                            borderColor: "rgba(57,255,20,0.45)",
                          }}
                        >
                          Confirmado
                        </span>
                      )}
                      {r.is_guest ? (
                        <span
                          className="label-caps text-[10px] px-1.5 py-0.5 border"
                          style={{ color: "#F59E0B", borderColor: "rgba(245,158,11,0.35)" }}
                        >
                          Convidado
                        </span>
                      ) : (
                        <span className="label-caps text-[10px] text-white/60">Membro</span>
                      )}
                    </div>
                    <div className="text-white font-semibold break-words">{r.rider_name}</div>
                    {r.confirmation_code && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="label-caps text-[10px] text-white/50">Código</span>
                        <span
                          className="font-display tracking-[0.2em] text-sm"
                          style={{ color: "#39FF14" }}
                          data-testid={`rsvp-code-mobile-${r.id}`}
                        >
                          {r.confirmation_code}
                        </span>
                      </div>
                    )}
                    {r.phone && (
                      <div className="text-white/60 text-xs mt-0.5">
                        📱 {r.phone}
                      </div>
                    )}
                    <div className="text-white/70 text-sm mt-1 break-words">
                      🏍️ {r.motorcycle_model}
                    </div>
                    {r.companion && (
                      <div className="text-white/60 text-sm mt-0.5 break-words">
                        + {r.companion}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => deleteRsvp(r)}
                    className="border border-white/15 hover:border-red-500 hover:text-red-400 p-2 rounded-sm transition-colors shrink-0"
                    data-testid={`rsvp-delete-${r.id}`}
                    aria-label="Excluir confirmado"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
